import { useState } from 'react';
import type { PunchListResponse, PunchPin, PunchPriority } from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { PhotoPicker } from './punchUi';
import { usePunchItemForm } from './usePunchItemForm';

/**
 * Add an item from the desktop: a before photo (required), what needs fixing,
 * the trade, and where. Leaving "Assigned to" on the trade default lets the
 * server fill it in from the job's trade → sub map. The form and its
 * retry-safe save are shared with the phone's step-by-step add
 * (usePunchItemForm).
 */
export default function PunchAddItemModal({ projectId, data, jobNumber, jobFolderUrl, pinned, onClose, onAdded }: {
  projectId: number;
  data: PunchListResponse;
  jobNumber: string;
  jobFolderUrl: string | null;
  /** Dropped on a drawing: where the item goes, the room suggested there, and where that is. */
  pinned?: { pin: PunchPin; room: string | null; where: string } | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const f = usePunchItemForm({
    projectId, data, jobNumber, jobFolderUrl, onAdded,
    initialPin: pinned?.pin ?? null, initialRoom: pinned?.room ?? '',
  });
  const [added, setAdded] = useState<string[]>([]);

  const save = async (another: boolean) => {
    const item = await f.save();
    if (!item) return;
    if (another) {
      setAdded((a) => [...a, item.label]);
      f.reset(true);
    } else {
      onClose();
    }
  };

  return (
    <Modal
      isOpen onClose={onClose} title="Add punch item" maxWidth={640} closeOnBackdropClick={!f.busy}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={f.busy}>Cancel</Button>
          <Button variant="secondary" onClick={() => save(true)} isLoading={f.busy} disabled={!f.subsQ.isSuccess}>Save &amp; Add Another</Button>
          <Button variant="primary" onClick={() => save(false)} isLoading={f.busy} disabled={!f.subsQ.isSuccess}>Save</Button>
        </>
      )}
    >
      <div className="pl-form">
        {added.length > 0 && <div className="pl-note">Added {added.join(', ')}. The room, and the field leader, are kept for the next one.</div>}
        {f.pin && pinned && (
          <div className="pl-note pl-row" style={{ justifyContent: 'space-between' }}>
            <span>📍 Pinned on {pinned.where}{pinned.room ? `. Room ${pinned.room} is from the drawing — change it if it’s wrong.` : '.'}</span>
            <button type="button" className="pl-link" disabled={f.busy} onClick={() => f.setPin(null)}>Don’t pin</button>
          </div>
        )}
        <PhotoPicker label="Before photo" file={f.photo} onChange={f.setPhoto} required />
        <label className="pl-field"><span>What needs fixing</span>
          <textarea value={f.description} maxLength={1000} placeholder="e.g. Fill caulk line along the south wall"
            onChange={(e) => f.setDescription(e.target.value)} />
        </label>
        <div className="pl-grid2">
          <label className="pl-field"><span>Trade</span>
            <select value={f.trade} onChange={(e) => f.setTrade(e.target.value)}>
              {f.trades.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="pl-field"><span>Assigned to</span>
            <AssigneeSelect f={f} data={data} />
          </label>
          <label className="pl-field"><span>Room or area</span>
            <input value={f.room} maxLength={60} placeholder="104, Atrium, General" onChange={(e) => f.setRoom(e.target.value)} />
          </label>
          <label className="pl-field"><span>Field leader</span>
            <LeaderSelect f={f} />
          </label>
          <label className="pl-field"><span>Due</span>
            <input type="date" value={f.due} onChange={(e) => f.setDue(e.target.value)} />
          </label>
          <label className="pl-field"><span>Priority</span>
            <select value={f.priority} onChange={(e) => f.setPriority(e.target.value as PunchPriority | '')}>
              <option value="">List default</option>
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
            </select>
          </label>
        </div>
        <label className="pl-field"><span>Notes</span>
          <textarea value={f.notes} maxLength={1000} placeholder="Which wall, access, a spec reference — anything else"
            onChange={(e) => f.setNotes(e.target.value)} />
        </label>
        <p className="pl-muted" style={{ margin: 0 }}>A blank due date uses the list’s default.</p>
        {f.subsQ.isError && !f.error && (
          <div className="pl-error">Couldn’t load this job’s subs and field leaders. Close and try again.</div>
        )}
        {f.error && <div className="pl-error">{f.error}</div>}
      </div>
    </Modal>
  );
}

/** "Assigned to": the trade default, nobody, the E&W crew, or a name from the job's list. */
export function AssigneeSelect({ f, data, className }: {
  f: ReturnType<typeof usePunchItemForm>; data: PunchListResponse; className?: string;
}) {
  return (
    <select className={className} value={f.assignee} onChange={(e) => f.setAssignee(e.target.value)}>
      <option value="default">{f.mappedSub ? `${f.mappedSub} (trade default)` : 'Trade default — none set'}</option>
      <option value="none">Unassigned</option>
      <option value="ew_crew">{data.ewCrewLabel}</option>
      {f.subNames.map((n) => <option key={n} value={`sub:${n}`}>{n}</option>)}
    </select>
  );
}

/** Field leader: the job's field leaders; "Pick…" until one is chosen when there are several. */
export function LeaderSelect({ f, className }: { f: ReturnType<typeof usePunchItemForm>; className?: string }) {
  return (
    <select className={className} value={f.leader} disabled={f.leaders.length === 0} onChange={(e) => f.setLeaderPick(e.target.value)}>
      {f.leaders.length === 0 && <option value="">No field leader on this job</option>}
      {f.leaders.length > 1 && !f.leader && <option value="">Pick the field leader…</option>}
      {f.leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
  );
}
